import { useMutation } from '@apollo/client';
import { Trans, t } from '@lingui/macro';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';

import { phoneNumberMask } from 'Components/Form/Form.helpers';
import Modal from 'common/Modal/Modal';
import emailIcon from 'common/icons/general/icon_email.svg';
import phoneIcon from 'common/icons/general/icon_phone.svg';
import config from 'config/config';
import { Button } from 'uikit/Button';
import { Checkbox } from 'uikit/Checkbox';
import FormRow from 'uikit/FormRow/FormRow';
import Input from 'uikit/Input/Input';
import { useSnackbar } from 'uikit/Snackbar';
import { Textarea } from 'uikit/Textarea';
import analytics from 'utils/analytics/analytics';
import { getResponseError } from 'utils/api';

import {
  COOPERATION_TYPES,
  sendReferralMutation,
  SendReferralResponse,
  referralValidationSchema,
  SendReferralsVars,
} from './Referral.helpers';
import './ReferralForm.scss';

type Props = {
  isFormOpen: boolean;
  onClose: () => void;
};

const ReferralForm: FC<Props> = (props) => {
  const [sendReferral] = useMutation<SendReferralResponse, SendReferralsVars>(sendReferralMutation);
  const { showSnackbar } = useSnackbar();

  const { values, errors, touched, isSubmitting, setSubmitting, handleChange, handleSubmit } = useFormik({
    initialValues: {
      fullName: '',
      linkUrl: '',
      linkCounter: '',
      cooperationTypes: [],
      phone: '',
      email: '',
      content: '',
    },
    validationSchema: referralValidationSchema,
    onSubmit: () => {
      analytics.global.referralSubmit();

      sendReferral({
        variables: {
          ...values,
        },
      })
        .then(() => {
          analytics.global.referralSuccess();
          // eslint-disable-next-line max-len
          const message = t`Dziękujemy za wysłanie wiadomości! Skontaktujemy się z Tobą w ciągu tygodnia, by omówić szczegóły współpracy.`;
          showSnackbar({
            message,
            variant: 'success',
          });
          props.onClose();
        })
        .catch((error) => {
          analytics.global.referralError();
          showSnackbar({
            message: getResponseError(error),
            variant: 'error',
          });
        })
        .finally(() => setSubmitting(false));
    },
  });

  useEffect(() => {
    if (errors) {
      analytics.global.referralError();
    }
  }, [errors]);

  return (
    <Modal
      title={<Trans>Opowiedz nam o sobie</Trans>}
      isOpen={props.isFormOpen}
      handleClose={props.onClose}
      className="referralFormModal"
    >
      <div className="referralForm">
        <form className="providerSignup__form" onSubmit={handleSubmit}>
          <FormRow>
            <Input
              name="fullName"
              id="full-name"
              type="text"
              value={values.fullName}
              error={touched.fullName && errors.fullName}
              autoCorrect="off"
              autoCapitalize="off"
              placeholder={t`Imię i nazwisko`}
              label={t`Imię i nazwisko`}
              onChange={handleChange}
              isBig
            />
          </FormRow>
          <FormRow>
            <Input
              name="linkUrl"
              id="link-url"
              type="url"
              value={values.linkUrl}
              error={touched.linkUrl && errors.linkUrl}
              autoCorrect="off"
              autoCapitalize="off"
              placeholder={t`Adres WWW`}
              label={t`Twoja strona WWW / kanał YouTube`}
              onChange={handleChange}
              isBig
            />
          </FormRow>
          <FormRow>
            <Input
              name="linkCounter"
              id="link-counter"
              type="text"
              inputMode="numeric"
              value={values.linkCounter}
              error={touched.linkCounter && errors.linkCounter}
              autoCorrect="off"
              autoCapitalize="off"
              placeholder={t`Wpisz liczbę`}
              label={t`Liczba miesięcznych odwiedzin strony / subskrybentów`}
              onChange={handleChange}
              isBig
            />
          </FormRow>
          <FormRow>
            <fieldset className="referralForm__cooperationTypes">
              <legend className="referralForm__cooperationTypesLabel">
                <Trans>Preferowana forma współpracy</Trans>
              </legend>
              {COOPERATION_TYPES.map((type, index) => (
                <Checkbox
                  name="cooperationTypes"
                  value={`type${index + 1}`}
                  id={`type${index + 1}`}
                  label={type}
                  key={type}
                  className="referralForm__cooperationType"
                  onChange={handleChange}
                />
              ))}
            </fieldset>
          </FormRow>
          <FormRow>
            <Input
              name="phone"
              id="phone-input"
              type="tel"
              inputMode="numeric"
              value={values.phone}
              error={touched.phone && errors.phone}
              mask={phoneNumberMask}
              placeholder="+48 ___ ___ ___"
              label={t`Numer telefonu`}
              iconUrl={phoneIcon}
              onChange={handleChange}
              isBig
            />
          </FormRow>
          <FormRow>
            <Input
              name="email"
              id="email-input"
              type="email"
              value={values.email}
              error={touched.email && errors.email}
              placeholder="E-mail"
              label={t`E-mail`}
              iconUrl={emailIcon}
              onChange={handleChange}
              isBig
            />
          </FormRow>
          <FormRow>
            <Textarea
              name="content"
              id="content-input"
              value={values.content}
              error={touched.content && errors.content}
              placeholder={t`Wpisz treść tutaj`}
              label={t`Treść wiadomości`}
              className="referralForm__content"
              onChange={handleChange}
            />
          </FormRow>
          <div className="referralForm__tos">
            <p className="referralForm__tosTerms">
              <Trans>
                Poprzez kliknięcie “Napisz do nas” wyrażasz dobrowolną zgodę na przedstawienie oferty nawiązania
                współpracy we wskazanym powyżej zakresie poprzez wiadomość e-mail i telefon oraz akceptujesz{' '}
                <a href={config.HELPDESK_TOS_URL} target="_blank" rel="noreferrer">
                  Regulamin
                </a>{' '}
                i przyjmujesz do wiadomości, że Twoje dane będą przetwarzane zgodnie z{' '}
                <a href={config.HELPDESK_PRIVACY_MEDIA_URL} target="_blank" rel="noreferrer">
                  Polityką prywatności
                </a>{' '}
                oraz{' '}
                <a href={config.HELPDESK_COOKIE_URL} target="_blank" rel="noreferrer">
                  Polityką dotyczącą plików cookie i podobnych technologii
                </a>
                .
              </Trans>
            </p>
          </div>

          <Button kind="cta" width="full" type="submit" isLoading={isSubmitting}>
            <Trans>Napisz do nas</Trans>
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default ReferralForm;
